import {
  BookOpenIcon,
  CalendarIcon,
  CashIcon,
  ChatIcon,
  ClipboardListIcon,
  CodeIcon,
  CursorClickIcon,
  DesktopComputerIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  HeartIcon,
  LinkIcon,
  MailIcon,
  SearchIcon,
  ShieldCheckIcon,
  UserAddIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import type { IndustryContent } from '../types';
import KnowledgeSources from '../visuals/KnowledgeSources';

export function useHrContent(): IndustryContent {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');

  return {
    key: 'hr',
    seoTitle: t('HR Chatbot: AI Agent That Answers Employee Questions'),
    seoDescription: t(
      'Give every employee instant answers about leave, benefits and policies. An AI HR agent trained on your handbook that answers in Slack and on your intranet, day and night.',
    ),
    hero: {
      title: t('HR answers in seconds.'),
      accent: t('Straight from your handbook, right in Slack.'),
      lead: t(
        'Train an agent on your handbook, benefits guides and policies. Employees ask in Slack or on your intranet and get a clear answer straight away, and your HR team gets its time back.',
      ),
      description: t(
        'Example: an employee asks the HR agent in a Slack direct message how to request parental leave and how many weeks they get, and it answers from the employee handbook.',
      ),
      surface: 'slack',
      person: { name: 'Sam Rivera', initials: 'SR' },
      brand: {
        name: t('HR Assistant'),
        initial: 'H',
        kind: 'Slack',
        bg: 'bg-[#3d5a80]',
      },
      messages: [
        {
          from: 'visitor',
          text: tc('How do I request parental leave?'),
          at: 600,
        },
        {
          from: 'agent',
          typingAt: 1000,
          at: 1800,
          content: tc(
            'Send the form in the HR portal at least eight weeks ahead. Here is the link.',
          ),
        },
        {
          from: 'visitor',
          text: t('Thanks! And how many weeks do I get?'),
          at: 2800,
        },
        {
          from: 'agent',
          typingAt: 3200,
          at: 4000,
          content: t(
            '16 weeks at full pay, then 10 more at half pay. You can take them in up to two blocks.',
          ),
        },
      ],
      steps: [
        {
          icon: SearchIcon,
          title: t('Searched the employee handbook'),
          via: tc('Knowledge'),
          at: 1200,
        },
        {
          icon: DocumentTextIcon,
          title: t('Found the parental leave policy'),
          via: t('Employee handbook.pdf'),
          at: 1500,
        },
        {
          icon: CashIcon,
          title: t('Checked the leave pay rules'),
          via: t('Benefits guide.pdf'),
          at: 3500,
        },
      ],
    },
    jobs: {
      title: t('The questions HR answers every week.'),
      tail: t('Now answered at once.'),
      lead: t(
        'Leave, benefits, pay dates, expenses. Most HR questions already have an answer in a document. Your agent finds it in seconds, so your team can focus on the conversations that need a person.',
      ),
      items: [
        {
          icon: CalendarIcon,
          title: t('Leave and time off'),
          detail: t(
            'Explains leave policies, how to request time off and who approves it.',
          ),
        },
        {
          icon: HeartIcon,
          title: t('Benefits'),
          detail: t(
            'Answers questions about health cover, pensions and perks from your benefits guides.',
          ),
        },
        {
          icon: CashIcon,
          title: t('Pay and expenses'),
          detail: t(
            'Shares pay dates, expense rules and how to submit a claim.',
          ),
        },
        {
          icon: UserAddIcon,
          title: t('Onboarding'),
          detail: t(
            'Helps new starters find their way around tools, policies and first-week tasks.',
          ),
        },
        {
          icon: ShieldCheckIcon,
          title: t('Policies'),
          detail: t(
            'Answers questions about remote work, travel and conduct from your handbook.',
          ),
        },
        {
          icon: DesktopComputerIcon,
          title: t('IT and office'),
          detail: t(
            'Answers questions about laptops, access and the office from your internal guides.',
          ),
        },
      ],
    },
    workflow: {
      title: t('Trained on your HR documents.'),
      tail: t('Answering where your team already works.'),
      learns: [
        { icon: BookOpenIcon, label: t('Employee handbook') },
        { icon: HeartIcon, label: t('Benefits guides') },
        { icon: ClipboardListIcon, label: t('Policies and forms') },
        { icon: GlobeAltIcon, label: t('Intranet pages') },
      ],
      acts: [
        { icon: CursorClickIcon, label: tc('Buttons') },
        { icon: ClipboardListIcon, label: tc('Forms') },
        { icon: MailIcon, label: tc('Email') },
        { icon: CodeIcon, label: tc('Custom actions') },
      ],
      channels: [
        { icon: 'FaSlack', label: 'Slack' },
        { icon: ChatIcon, label: t('Chat on your intranet') },
        { icon: LinkIcon, label: tc('Shareable link') },
        { icon: 'FaEnvelope', label: tc('Email') },
        { icon: 'FaTelegram', label: 'Telegram' },
      ],
    },
    spotlight: {
      title: t('When a policy changes,'),
      tail: t('so do the answers.'),
      lead: t(
        'Upload the new version and your agent answers from it as soon as it’s ready. Add a Q&A for anything your documents don’t cover, and retrain your intranet pages with one click.',
      ),
      points: [
        t('PDF, Word, CSV, TXT and EPUB files'),
        t('Intranet pages and sitemaps'),
        t('Q&A pairs for answers that must be exact'),
        t('Weekly auto-retrain on Unlimited 500 and up'),
      ],
      description: t(
        'Example: the agent’s knowledge page with the employee handbook, the benefits guide, intranet pages and a Q&A, and a new version of a policy being processed.',
      ),
      visual: <KnowledgeSources />,
    },
    faqs: [
      {
        question: t('Can employees ask it in Slack?'),
        answer: t(
          'Yes. Add the agent to your Slack workspace and people can message it directly or @mention it in a channel. Slack works on every plan, including Free.',
        ),
      },
      {
        question: t(
          'Can it answer questions about someone’s own salary or leave balance?',
        ),
        answer: t(
          'Only if you connect it to a system that holds that information, with a custom action. Trained on documents alone, it answers policy questions, not personal ones.',
        ),
      },
      {
        question: t('How do we keep answers up to date?'),
        answer: t(
          'Upload the new version of a document and delete the old one, or retrain your intranet pages with one click. On Unlimited 500 and up, website pages are checked for changes every week.',
        ),
      },
      {
        question: t('What happens when it can’t answer?'),
        answer: t(
          'Your instructions decide. It can point people to the right HR contact, or take their question and email it to your HR team.',
        ),
      },
      {
        question: t('Can we use it on our intranet?'),
        answer: t(
          'Yes. Add the chat to your intranet with one line of code, or share a link to a full-page chat.',
        ),
      },
    ],
    finalExchange: {
      question: t('When is payday this month?'),
      answer: t('On Friday the 28th, as usual.'),
    },
    finalTitle: t('Your HR agent could be answering questions by lunchtime.'),
  };
}
